/**
 * usePublicBookingV2 — state machine for the OTP-verified clinic+type
 * booking flow.
 *
 * URL: /book/c/:clinicSlug/t/:typeSlug
 *
 * Steps (driven by the state machine, NOT by the user clicking forward):
 *   loading
 *     -> error                              (resolution failed)
 *     -> select-practitioner                (multiple eligible providers)
 *     -> select-date                        (single eligible provider)
 *   select-date -> select-time
 *   select-time -> enter-email
 *   enter-email -> verify-otp               (always; even on invalid target,
 *                                            anti-enumeration)
 *   verify-otp -> confirm-dob               (patient_status === 'existing')
 *               -> onboarding-form          (patient_status === 'new')
 *               -> ambiguous                (patient_status === 'ambiguous')
 *   confirm-dob (3 fails) -> onboarding-form
 *   confirm-dob (success) -> consent-check  (existing flow component)
 *               -> confirmation             (after consent + book)
 *   onboarding-form -> consent-check -> confirmation
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { startOfMonth, format } from 'date-fns';
import { z } from 'zod';
import {
  publicBookingV2Service,
  type ClinicTypeBookingPage,
  type TimeSlot,
  type AvailableDate,
  type PatientStatus,
  type OtpBookingConfirmation,
} from '@/services/bookingService';
import type { patientFormPublicSchema } from '@/validators/patientFormPublic.schema';

type PatientFormData = z.infer<typeof patientFormPublicSchema>;

export type BookingV2Step =
  | 'loading'
  | 'error'
  | 'select-practitioner'
  | 'select-date'
  | 'select-time'
  | 'enter-email'
  | 'verify-otp'
  | 'confirm-dob'
  | 'onboarding-form'
  | 'consent-check'
  | 'payment'
  | 'ambiguous'
  | 'confirmation';

const browserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago';
  } catch {
    return 'America/Chicago';
  }
};

const extractApiError = (err: unknown, fallback: string): string => {
  const e = err as {
    response?: { data?: { error?: { message?: string; code?: string } } };
    message?: string;
  };
  return e?.response?.data?.error?.message || e?.message || fallback;
};

const extractApiErrorCode = (err: unknown): string | null => {
  const e = err as { response?: { data?: { error?: { code?: string } } } };
  return e?.response?.data?.error?.code || null;
};

const extractAttemptsRemaining = (err: unknown): number | null => {
  const e = err as { response?: { data?: { error?: { attempts_remaining?: number } } } };
  const v = e?.response?.data?.error?.attempts_remaining;
  return typeof v === 'number' ? v : null;
};

export interface UsePublicBookingV2Return {
  // routing / page state
  step: BookingV2Step;
  setStep: (s: BookingV2Step) => void;
  error: string | null;

  // resolved page data
  pageData: ClinicTypeBookingPage | null;

  // selections
  selectedPractitionerId: string | null;
  selectedDate: Date | undefined;
  selectedSlot: TimeSlot | null;

  // calendar
  currentMonth: Date;
  setCurrentMonth: (d: Date) => void;
  availableDates: string[];
  loadingDates: boolean;
  availableSlots: TimeSlot[];
  loadingSlots: boolean;
  providerTimezone: string | null;
  visitorTimezone: string;
  setVisitorTimezone: (tz: string) => void;

  // OTP / session
  email: string;
  sessionRef: string | null;
  sessionToken: string | null;
  patientStatus: PatientStatus | null;
  patientPreview: { first_name: string; middle_name?: string | null; last_name: string } | null;
  dobAttemptsRemaining: number | null;

  // Bound patient (after either confirm-dob or onboard succeeds).
  // Drives the consent-check step.
  boundPatientId: string | null;
  boundPatientName: string | null;

  // confirmation
  confirmation: OtpBookingConfirmation | null;

  // status flags
  loadingAction: boolean;
  actionError: string | null;

  // selectors
  selectedPractitioner: ClinicTypeBookingPage['practitioners'][number] | null;

  // handlers
  handleSelectPractitioner: (id: string) => void;
  handleSelectDate: (d: Date | undefined) => void;
  handleSelectSlot: (slotStart: string) => void;
  handleRequestOtp: (email: string) => Promise<void>;
  handleResendOtp: () => Promise<void>;
  handleChangeEmail: () => void;
  handleVerifyOtp: (otp: string) => Promise<void>;
  handleConfirmDob: (dob: string) => Promise<void>;
  handleOnboardAndConfirm: (data: PatientFormData) => Promise<void>;
  handleConsentComplete: () => Promise<void>;
  handlePaymentComplete: (paymentMethodId: string) => Promise<void>;
  handleConsentCancel: () => void;
  handleBack: () => void;
  formatSlotTime: (slot: TimeSlot, tz: string) => string;
}

export function usePublicBookingV2(): UsePublicBookingV2Return {
  const { clinicSlug, typeSlug } = useParams<{ clinicSlug: string; typeSlug: string }>();
  const [searchParams] = useSearchParams();
  const preselectedPractitionerSlug = searchParams.get('p') || null;
  // Guided-intake source token (from the post-intake booking email). When
  // present it lets the backend widen the OTP patient-match to include
  // external_patients grants, so returning patients skip re-onboarding.
  const sourceToken = searchParams.get('token') || null;
  // Nimbus-routed bookings (from a guided HRT/TRT intake) bypass per-visit
  // payment — the patient has already been billed via the intake checkout.
  const isNimbusRouted = searchParams.get('src') === 'nimbus';

  // ── Page resolution ────────────────────────────────────────────────────────
  const [step, setStep] = useState<BookingV2Step>('loading');
  const [error, setError] = useState<string | null>(null);
  const [pageData, setPageData] = useState<ClinicTypeBookingPage | null>(null);

  // ── Selections ─────────────────────────────────────────────────────────────
  const [selectedPractitionerId, setSelectedPractitionerId] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);

  // ── Calendar ───────────────────────────────────────────────────────────────
  const [currentMonth, setCurrentMonth] = useState<Date>(startOfMonth(new Date()));
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [providerTimezone, setProviderTimezone] = useState<string | null>(null);
  const [visitorTimezone, setVisitorTimezone] = useState<string>(browserTimezone);

  // ── OTP / session ──────────────────────────────────────────────────────────
  const [email, setEmail] = useState('');
  const [sessionRef, setSessionRef] = useState<string | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [patientStatus, setPatientStatus] = useState<PatientStatus | null>(null);
  const [patientPreview, setPatientPreview] = useState<
    { first_name: string; middle_name?: string | null; last_name: string } | null
  >(null);
  const [dobAttemptsRemaining, setDobAttemptsRemaining] = useState<number | null>(null);
  const [boundPatientId, setBoundPatientId] = useState<string | null>(null);
  const [boundPatientName, setBoundPatientName] = useState<string | null>(null);

  // ── Confirmation ───────────────────────────────────────────────────────────
  const [confirmation, setConfirmation] = useState<OtpBookingConfirmation | null>(null);

  // ── Action UX ──────────────────────────────────────────────────────────────
  const [loadingAction, setLoadingAction] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Stable per-flow identifier used as the slot-hold key. Survives email
  // changes AND page refreshes so the same browser tab reuses the same hold.
  // Stored in sessionStorage keyed by the booking URL so different tabs get
  // independent flow IDs but a refresh within the same tab keeps the same one.
  const bookingFlowId = useRef<string>(null!);
  if (!bookingFlowId.current) {
    const storageKey = `booking_flow_id:${clinicSlug}:${typeSlug}`;
    const existing = sessionStorage.getItem(storageKey);
    if (existing) {
      bookingFlowId.current = existing;
    } else {
      const id = crypto.randomUUID();
      sessionStorage.setItem(storageKey, id);
      bookingFlowId.current = id;
    }
  }

  // ── Resolve clinic+type ────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      if (!clinicSlug || !typeSlug) {
        setError('Invalid booking link');
        setStep('error');
        return;
      }
      try {
        const data = await publicBookingV2Service.getClinicTypePage(
          clinicSlug,
          typeSlug,
          preselectedPractitionerSlug,
        );
        setPageData(data);

        // Auto-pick practitioner if there's only one or if URL preselects.
        let chosenId: string | null = null;
        if (preselectedPractitionerSlug) {
          const match = data.practitioners.find(
            p => p.booking_slug === preselectedPractitionerSlug,
          );
          if (match) chosenId = match.id;
        }
        if (!chosenId && data.practitioners.length === 1) {
          chosenId = data.practitioners[0].id;
        }

        if (chosenId) {
          setSelectedPractitionerId(chosenId);
          setStep('select-date');
        } else {
          setStep('select-practitioner');
        }
      } catch (e) {
        setError(extractApiError(e, 'This booking page is not available'));
        setStep('error');
      }
    };
    load();
  }, [clinicSlug, typeSlug, preselectedPractitionerSlug]);

  // ── Load month dates ───────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      if (!clinicSlug || !typeSlug || !selectedPractitionerId) return;
      if (step === 'loading' || step === 'error' || step === 'select-practitioner') return;
      setLoadingDates(true);
      try {
        const result = await publicBookingV2Service.getDates(
          clinicSlug,
          typeSlug,
          selectedPractitionerId,
          currentMonth.getFullYear(),
          currentMonth.getMonth() + 1,
          visitorTimezone,
        );
        setAvailableDates(result.dates.filter((d: AvailableDate) => d.hasSlots).map(d => d.date));
      } catch (e) {
        // Soft-fail; the calendar will just show no dates.
        console.error('[booking-v2] dates load failed', e);
      } finally {
        setLoadingDates(false);
      }
    };
    load();
  }, [clinicSlug, typeSlug, selectedPractitionerId, currentMonth, step, visitorTimezone]);

  // ── Load slots for selected date ───────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      if (!clinicSlug || !typeSlug || !selectedPractitionerId || !selectedDate) return;
      setLoadingSlots(true);
      setAvailableSlots([]);
      try {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const result = await publicBookingV2Service.getSlots(
          clinicSlug,
          typeSlug,
          selectedPractitionerId,
          dateStr,
          visitorTimezone,
        );
        setProviderTimezone(result.timezone);
        const sorted = [...result.slots].sort(
          (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
        );
        setAvailableSlots(sorted);
      } catch (e) {
        console.error('[booking-v2] slots load failed', e);
      } finally {
        setLoadingSlots(false);
      }
    };
    load();
  }, [clinicSlug, typeSlug, selectedPractitionerId, selectedDate, visitorTimezone]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleSelectPractitioner = useCallback((id: string) => {
    setSelectedPractitionerId(id);
    setStep('select-date');
  }, []);

  const handleSelectDate = useCallback((d: Date | undefined) => {
    setSelectedDate(d);
    setSelectedSlot(null);
    if (d) setStep('select-time');
  }, []);

  const handleSelectSlot = useCallback(
    (slotStart: string) => {
      const slot = availableSlots.find(s => s.start === slotStart);
      if (!slot) return;
      setSelectedSlot(slot);
      setStep('enter-email');
    },
    [availableSlots],
  );

  const handleRequestOtp = useCallback(
    async (submittedEmail: string) => {
      if (!pageData || !selectedPractitionerId || !selectedSlot) return;
      setLoadingAction(true);
      setActionError(null);
      try {
        const res = await publicBookingV2Service.requestOtp({
          email: submittedEmail,
          clinic_id: pageData.clinic.id,
          appointment_type_id: pageData.appointment_type.id,
          practitioner_id: selectedPractitionerId,
          slot_start: selectedSlot.start,
          slot_end: selectedSlot.end,
          timezone: visitorTimezone,
          booking_flow_id: bookingFlowId.current,
        });
        setEmail(submittedEmail);
        // session_ref may be undefined when the target was invalid; we
        // intentionally still transition to verify-otp so the response shape
        // doesn't leak target validity.
        setSessionRef(res.session_ref || null);
        setStep('verify-otp');
      } catch (e) {
        // 409 (slot held) / 400 (validation) — surface to the email step.
        setActionError(extractApiError(e, 'Could not send verification code'));
      } finally {
        setLoadingAction(false);
      }
    },
    [pageData, selectedPractitionerId, selectedSlot, visitorTimezone],
  );

  const handleResendOtp = useCallback(async () => {
    if (!email) return;
    setActionError(null);
    await handleRequestOtp(email);
  }, [email, handleRequestOtp]);

  const handleChangeEmail = useCallback(() => {
    setSessionRef(null);
    setSessionToken(null);
    setPatientStatus(null);
    setPatientPreview(null);
    setActionError(null);
    setStep('enter-email');
  }, []);

  const handleVerifyOtp = useCallback(
    async (otp: string) => {
      if (loadingAction) return;
      setLoadingAction(true);
      setActionError(null);
      try {
        if (!sessionRef) {
          // Anti-enumeration: target was invalid; surface a generic OTP error.
          throw new Error('That code didn\u2019t match. Please try again.');
        }
        const res = await publicBookingV2Service.verifyOtp(sessionRef, otp, sourceToken || undefined);
        setSessionToken(res.session_token);
        setPatientStatus(res.patient_status);
        setPatientPreview(res.patient_preview);
        if (res.patient_status === 'existing') {
          setStep('confirm-dob');
        } else if (res.patient_status === 'ambiguous') {
          setStep('ambiguous');
        } else {
          setStep('onboarding-form');
        }
      } catch (e) {
        setActionError(extractApiError(e, 'That code didn\u2019t match. Please try again.'));
      } finally {
        setLoadingAction(false);
      }
    },
    [sessionRef, loadingAction, sourceToken],
  );

  const handleConfirmDob = useCallback(
    async (dob: string) => {
      if (!sessionToken) return;
      setLoadingAction(true);
      setActionError(null);
      try {
        const res = await publicBookingV2Service.confirmDob(sessionToken, dob);
        setDobAttemptsRemaining(null);
        setBoundPatientId(res.patient_id);
        setBoundPatientName(`${res.patient.first_name} ${res.patient.last_name}`.trim());
        // Identity proven — drive the consent gate against the matched
        // patient_id before the final /confirm step actually books.
        setStep('consent-check');
        return;
      } catch (e) {
        const code = extractApiErrorCode(e);
        const remaining = extractAttemptsRemaining(e);
        setDobAttemptsRemaining(remaining);
        if (code === 'DOB_FALLTHROUGH') {
          // Backend dropped the match; route to onboarding to capture a
          // fresh record (clinic-side review will reconcile if needed).
          setActionError(null);
          setPatientStatus('new');
          setPatientPreview(null);
          setStep('onboarding-form');
        } else {
          setActionError(extractApiError(e, 'Date of birth did not match'));
        }
      } finally {
        setLoadingAction(false);
      }
    },
    [sessionToken],
  );

  /**
   * Called by the consent gate after the patient finishes any required
   * consent forms. This is the actual booking commit — both identity AND
   * consent are now in place.
   */
  const handleConsentComplete = useCallback(async () => {
    if (!sessionToken) return;
    // Guard against duplicate fires. ConsentGate re-runs its check effect
    // under React Strict Mode (and after state changes), which was causing
    // two parallel POST /confirm calls on the same session and a self-
    // deadlock on the patient_action_links row lock.
    if (loadingAction) return;
    // If the appointment type has a price, route to payment before confirming.
    // Nimbus-routed bookings skip this — the visit was paid for at intake.
    if (
      !isNimbusRouted &&
      pageData?.appointment_type.price_cents &&
      pageData.appointment_type.price_cents > 0
    ) {
      setStep('payment');
      return;
    }
    setLoadingAction(true);
    setActionError(null);
    try {
      const result = await publicBookingV2Service.confirm(sessionToken, {
        timezone: visitorTimezone,
      });
      setConfirmation(result);
      setStep('confirmation');
    } catch (e) {
      const code = extractApiErrorCode(e);
      if (code === 'SLOT_UNAVAILABLE') {
        setActionError('That time slot is no longer available. Please pick a new time.');
        setStep('select-time');
      } else {
        setActionError(extractApiError(e, 'Could not complete booking'));
      }
    } finally {
      setLoadingAction(false);
    }
  }, [sessionToken, visitorTimezone, loadingAction, pageData?.appointment_type.price_cents, isNimbusRouted]);

  /**
   * Called after the patient completes the payment step with a chosen
   * (or newly created) payment method. Sends the confirm call with the
   * payment_method_id attached.
   */
  const handlePaymentComplete = useCallback(
    async (paymentMethodId: string) => {
      if (!sessionToken) return;
      setLoadingAction(true);
      setActionError(null);
      try {
        const result = await publicBookingV2Service.confirm(sessionToken, {
          timezone: visitorTimezone,
          payment_method_id: paymentMethodId || undefined,
        });
        setConfirmation(result);
        setStep('confirmation');
      } catch (e) {
        const code = extractApiErrorCode(e);
        if (code === 'SLOT_UNAVAILABLE') {
          setActionError('That time slot is no longer available. Please pick a new time.');
          setStep('select-time');
        } else {
          setActionError(extractApiError(e, 'Could not complete booking'));
        }
      } finally {
        setLoadingAction(false);
      }
    },
    [sessionToken, visitorTimezone],
  );

  /**
   * Called when the patient backs out of consent. We don't have a clean
   * "go back to a different identity" target without losing the OTP
   * session, so we route them to the appropriate previous step.
   */
  const handleConsentCancel = useCallback(() => {
    if (patientStatus === 'existing') {
      setStep('confirm-dob');
    } else {
      setStep('onboarding-form');
    }
  }, [patientStatus]);

  /**
   * Phase 3 split: onboard the new patient, bind patient_id to the
   * session, and route to the consent gate. The actual booking commit
   * happens later in handleConsentComplete.
   */
  const handleOnboardAndConfirm = useCallback(
    async (data: PatientFormData) => {
      if (!sessionToken) return;
      setLoadingAction(true);
      setActionError(null);
      try {
        const res = await publicBookingV2Service.onboard(sessionToken, data);
        setBoundPatientId(res.patient_id);
        setBoundPatientName(`${res.patient.first_name} ${res.patient.last_name}`.trim());
        setStep('consent-check');
      } catch (e) {
        const code = extractApiErrorCode(e);
        if (code === 'PATIENT_ALREADY_EXISTS') {
          setActionError(
            'A record with these details already exists. Please contact the clinic to book.',
          );
        } else {
          setActionError(extractApiError(e, 'Could not complete onboarding'));
        }
      } finally {
        setLoadingAction(false);
      }
    },
    [sessionToken],
  );

  const handleBack = useCallback(() => {
    setActionError(null);
    switch (step) {
      case 'select-date':
        if ((pageData?.practitioners.length || 0) > 1) setStep('select-practitioner');
        break;
      case 'select-time':
        setStep('select-date');
        setSelectedSlot(null);
        setSelectedDate(undefined);
        break;
      case 'enter-email':
        setStep('select-time');
        break;
      case 'verify-otp':
        setStep('enter-email');
        break;
      case 'confirm-dob':
      case 'onboarding-form':
      case 'ambiguous':
        handleChangeEmail();
        break;
      case 'consent-check':
        // Bounce back to the identity step that fed us here.
        if (patientStatus === 'existing') setStep('confirm-dob');
        else setStep('onboarding-form');
        break;
      case 'payment':
        setStep('consent-check');
        break;
      default:
        break;
    }
  }, [step, pageData?.practitioners.length, handleChangeEmail, patientStatus]);

  const formatSlotTime = useCallback(
    (slot: TimeSlot, providerTz: string) => {
      try {
        if (!slot.start || !slot.end) return 'N/A';
        const tz = visitorTimezone || providerTz;
        const timeFmt: Intl.DateTimeFormatOptions = {
          timeZone: tz,
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        };
        const start = new Date(slot.local_start || slot.start);
        const end = new Date(slot.local_end || slot.end);
        return `${new Intl.DateTimeFormat('en-US', timeFmt).format(start)} - ${new Intl.DateTimeFormat('en-US', timeFmt).format(end)}`;
      } catch {
        return 'N/A';
      }
    },
    [visitorTimezone],
  );

  const selectedPractitioner = pageData?.practitioners.find(p => p.id === selectedPractitionerId)
    || null;

  return {
    step,
    setStep,
    error,
    pageData,
    selectedPractitionerId,
    selectedDate,
    selectedSlot,
    currentMonth,
    setCurrentMonth,
    availableDates,
    loadingDates,
    availableSlots,
    loadingSlots,
    providerTimezone,
    visitorTimezone,
    setVisitorTimezone,
    email,
    sessionRef,
    sessionToken,
    patientStatus,
    patientPreview,
    dobAttemptsRemaining,
    boundPatientId,
    boundPatientName,
    confirmation,
    loadingAction,
    actionError,
    selectedPractitioner,
    handleSelectPractitioner,
    handleSelectDate,
    handleSelectSlot,
    handleRequestOtp,
    handleResendOtp,
    handleChangeEmail,
    handleVerifyOtp,
    handleConfirmDob,
    handleOnboardAndConfirm,
    handleConsentComplete,
    handlePaymentComplete,
    handleConsentCancel,
    handleBack,
    formatSlotTime,
  };
}
